import type { ReceiptChargeKind } from '../../../generated/config-enums';

/**
 * Shared bill-line draft partitioning for the two bill save paths
 * (BillQuickCreateDialog + BillDetailPage inline editor).
 *
 * The bug this fixes: both paths filtered lines to `description && account_id`
 * before POST/PUT. The bill line editor's account column is hidden by default
 * and account_id is only back-filled by the vendor AI suggestion, so any line
 * entered afterward was silently dropped from the request while the on-screen
 * footer totals still counted it — the dialog, the header total, and the DB all
 * disagreed.
 *
 * Rule: a line the user has TOUCHED (a description or a non-zero price) must
 * never be dropped silently. If it lacks a description or an expense account it
 * is "incomplete" — the caller blocks/holds the save and surfaces validation
 * instead of quietly discarding it.
 */

/** Minimal shape needed to decide whether a line was entered / is complete. */
export interface BillLineDraftLike {
  description: string;
  account_id: string;
  unit_price: number;
}

/** A line the user has interacted with (vs. the empty trailing add-row). */
export function isEnteredBillLine(line: BillLineDraftLike): boolean {
  return line.description.trim() !== '' || line.unit_price !== 0;
}

export interface PartitionedBillLines<T extends BillLineDraftLike> {
  /**
   * The entered lines with `fallbackAccountId` applied to any that had no
   * account of their own. These are the lines to persist.
   */
  entered: T[];
  /** An entered line still missing a description or an expense account. */
  hasIncomplete: boolean;
}

/**
 * Split lines into the entered set (with the fallback account applied) and a
 * flag for whether any entered line is still incomplete.
 *
 * @param lines             all editor lines, including the empty trailing row
 * @param fallbackAccountId account applied to entered lines without one (the
 *                          vendor AI suggestion in the create dialog; omitted on
 *                          the detail editor, which has a visible account column)
 */
export function partitionBillLines<T extends BillLineDraftLike>(
  lines: T[],
  fallbackAccountId?: string,
): PartitionedBillLines<T> {
  const entered = lines
    .filter(isEnteredBillLine)
    .map((line) =>
      line.account_id ? line : { ...line, account_id: fallbackAccountId ?? '' },
    );
  const hasIncomplete = entered.some(
    (line) => line.description.trim() === '' || !line.account_id,
  );
  return { entered, hasIncomplete };
}

/**
 * Fields a bill line must carry back to the server on a draft save, beyond the
 * ones the user edits directly.
 *
 * `BillService._set_lines` REPLACES every line on save, so any field the payload
 * omits is not "left alone" — it is destroyed. These fields are therefore
 * round-tripped rather than resent from the editor:
 *
 * - `prices_include_tax` — the inclusive-tax ownership marker (WI fc8f6615);
 *   dropping it re-extracts VAT from an already-net price.
 * - `landed_cost_category_id` — the per-line landed-cost split (GAP-1), set on
 *   the Bill detail landed-cost panel and not editable in the line editor at
 *   all; dropping it silently wipes the split the moment any unrelated line
 *   field is edited.
 * - `purchase_order_line_id` / `goods_receipt_line_id` — the three-way-match
 *   linkage a bill built from a goods receipt carries. Dropping it is the worst
 *   of the three because it fails OPEN: the bill silently stops being matched to
 *   its order, so the variance panel reads "billed 0 / matched" and — since
 *   `_three_way_match_findings` returns nothing without a PO-line link — the
 *   approval hold stops firing. Editing any line quantity would otherwise have
 *   been a way to walk an over-billed invoice straight past the gate.
 * - `receipt_charge_kind` — which goods-receipt header charge (freight / misc /
 *   discount) the line materialises. It is the only linkage such a line has: it
 *   carries no PO line and no shipment charge, so dropping it sends the AP
 *   invoice audit back to reporting the charge as an unmatched exception on an
 *   amount the receipt itself recorded.
 */
export interface BillLineRoundTripLike {
  prices_include_tax?: boolean;
  landed_cost_category_id?: string | null;
  purchase_order_line_id?: string | null;
  goods_receipt_line_id?: string | null;
  receipt_charge_kind?: ReceiptChargeKind | null;
}

export interface BillLineRoundTripFields {
  prices_include_tax: boolean | undefined;
  /** `undefined` (omit) rather than `null`, so an unset line inherits the header. */
  landed_cost_category_id: string | undefined;
  /** The PO line this bill line pays for — the key the AP approval gate matches on. */
  purchase_order_line_id: string | undefined;
  /** The receipt line this bill line relieves (GRNI sourcing). */
  goods_receipt_line_id: string | undefined;
  /** Which receipt header charge this line materialises — the AP audit's only
   *  handle on it. */
  receipt_charge_kind: ReceiptChargeKind | undefined;
}

/**
 * The server-owned fields a draft line save must carry back untouched.
 * Extracted from the page so the round-trip is directly testable — it is
 * load-bearing (see above), not incidental.
 */
export function billLineRoundTripFields(
  line: BillLineRoundTripLike,
): BillLineRoundTripFields {
  return {
    prices_include_tax: line.prices_include_tax,
    landed_cost_category_id: line.landed_cost_category_id ?? undefined,
    purchase_order_line_id: line.purchase_order_line_id ?? undefined,
    goods_receipt_line_id: line.goods_receipt_line_id ?? undefined,
    receipt_charge_kind: line.receipt_charge_kind ?? undefined,
  };
}
